import axios from "axios"

export interface ClientMap {
  id?: number | string
  name: string
  client_id: string
  source_project_id: string
  source_deployment_id: string
  source_calibration_id: string
  destination_project_id: string
  destination_deployment_id: string
  destination_calibration_id: string
  is_enabled?: boolean
  is_deleted?: boolean
  created_at?: string
  updated_at?: string
}

export interface ClientMapsResponse {
  client_maps: ClientMap[]
}

export const normalizeClientMapsResponse = (value: unknown): ClientMapsResponse => {
  if (Array.isArray(value)) return { client_maps: value as ClientMap[] }

  if (value && typeof value === "object" && "client_maps" in value) {
    const clientMaps = (value as { client_maps?: unknown }).client_maps
    if (Array.isArray(clientMaps)) return { client_maps: clientMaps as ClientMap[] }
    if (clientMaps && typeof clientMaps === "object") {
      return { client_maps: [clientMaps as ClientMap] }
    }
  }

  return { client_maps: [] }
}

const getHeaders = (token: string) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
})

const isNotFoundError = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 404
}

export const clientMapsAPI = {
  createClientMap: async (input: {
    token: string
    apiGatewayUrl: string
    clientMap: ClientMap
  }) => {
    const { token, apiGatewayUrl, clientMap } = input
    const res = await axios.post(
      `${apiGatewayUrl}/clientMaps`,
      clientMap,
      {
        headers: getHeaders(token),
      }
    )
    return res.data as ClientMap
  },
  getClientMaps: async (input: {
    token: string
    apiGatewayUrl: string
    id?: string
    is_enabled?: boolean
    is_deleted?: boolean
  }) => {
    const { token, apiGatewayUrl, id, is_enabled, is_deleted } = input
    try {
      const res = await axios.get(
        `${apiGatewayUrl}/clientMaps`,
        {
          headers: getHeaders(token),
          params: {
            ...(id && { id }),
            ...(typeof is_enabled === "boolean" && { is_enabled }),
            ...(typeof is_deleted === "boolean" && { is_deleted }),
          },
        }
      )
      return normalizeClientMapsResponse(res.data)
    } catch (error) {
      if (isNotFoundError(error)) return { client_maps: [] }
      throw error
    }
  },
  updateClientMap: async (input: {
    token: string
    apiGatewayUrl: string
    clientMapId: string | number
    clientMap: Partial<ClientMap>
  }) => {
    const { token, apiGatewayUrl, clientMapId, clientMap } = input
    const res = await axios.put(
      `${apiGatewayUrl}/clientMaps/${clientMapId}`,
      clientMap,
      {
        headers: getHeaders(token),
      }
    )
    return res.data as ClientMap
  },
  deleteClientMap: async (input: {
    token: string
    apiGatewayUrl: string
    clientMapId: string | number
  }) => {
    const { token, apiGatewayUrl, clientMapId } = input
    const res = await axios.delete(
      `${apiGatewayUrl}/clientMaps/${clientMapId}`,
      {
        headers: getHeaders(token),
      }
    )
    return res.data
  },
}
